/**
 * The menu in the corner.
 *
 * Two entries and nothing else: the way to the full version, and the way to
 * pay for it. Everything that belongs to tuning a guitar lives in the settings
 * where a player looks for it — this is the shop counter, kept out of the way.
 */

import { useCallback, useEffect, useRef, useState } from 'react'
import { DonateDialog } from './DonateDialog'
import { UnlockDialog } from './UnlockDialog'
import { useTunerStore } from '../../state/tuner-store'

type Panel = 'unlock' | 'donate'

interface AppMenuProps {
  /** Injected in tests; the browser's fetch otherwise. */
  readonly fetcher?: typeof fetch
  /** Injected in tests; a real navigation otherwise. */
  readonly navigate?: (path: string) => void
}

export function AppMenu({ fetcher, navigate }: AppMenuProps): React.JSX.Element {
  const isDemo = useTunerStore((state) => state.edition === 'demo')
  const [isOpen, setOpen] = useState(false)
  const [panel, setPanel] = useState<Panel | null>(null)
  const triggerRef = useRef<HTMLButtonElement | null>(null)

  const closeMenu = useCallback(() => {
    setOpen(false)
  }, [])

  const closePanel = useCallback(() => {
    setPanel(null)
    triggerRef.current?.focus()
  }, [])

  // Clicking anywhere else, or Escape, puts the menu away — the panels have
  // their own handling, so this only runs while the bare menu is open.
  useEffect(() => {
    if (!isOpen) return undefined

    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') closeMenu()
    }
    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('keydown', onKeyDown)
    }
  }, [closeMenu, isOpen])

  const open = (next: Panel): void => {
    setPanel(next)
    setOpen(false)
  }

  return (
    <div className="app-menu">
      <button
        type="button"
        className="app-menu__trigger"
        ref={triggerRef}
        aria-expanded={isOpen}
        aria-haspopup="menu"
        aria-label="Menü"
        onClick={() => {
          setOpen((was) => !was)
        }}
      >
        {/* Three bars, drawn rather than typed: a glyph would inherit the
            monospace font and sit at the wrong weight. */}
        <span className="app-menu__bar" />
        <span className="app-menu__bar" />
        <span className="app-menu__bar" />
      </button>

      {isOpen && (
        <>
          <div className="app-menu__catcher" onClick={closeMenu} aria-hidden="true" />
          <ul className="app-menu__list" role="menu">
            {isDemo && (
              <li role="none">
                <button
                  type="button"
                  role="menuitem"
                  className="app-menu__item"
                  onClick={() => {
                    open('unlock')
                  }}
                >
                  Vollversion
                </button>
              </li>
            )}
            <li role="none">
              <button
                type="button"
                role="menuitem"
                className="app-menu__item"
                onClick={() => {
                  open('donate')
                }}
              >
                Spenden
              </button>
            </li>
          </ul>
        </>
      )}

      {panel === 'unlock' && (
        <UnlockDialog
          onClose={closePanel}
          {...(fetcher ? { fetcher } : {})}
          {...(navigate ? { navigate } : {})}
        />
      )}
      {panel === 'donate' && <DonateDialog onClose={closePanel} />}
    </div>
  )
}
